import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Player, PlayerAttributes, Position } from '../types';
import { apiClient } from '../api/client';
import { calculatePositionRatings } from '../utils/calculations';
import { useAuth } from './AuthContext';

interface PlayerContextType {
  players: Player[];
  loading: boolean;
  error: string | null;
  selectedPlayerIds: Set<string>;
  fetchPlayers: () => Promise<void>;
  addPlayer: (name: string, favoritePosition?: Position) => Promise<Player>;
  updatePlayer: (
    id: string,
    updates: {
      name?: string;
      favorite_position?: Position;
      attributes?: Partial<PlayerAttributes>;
    }
  ) => Promise<Player>;
  deletePlayer: (id: string) => Promise<void>;
  bulkDeletePlayers: (ids: string[]) => Promise<void>;
  togglePlayerSelection: (id: string) => void;
  selectAllPlayers: () => void;
  deselectAllPlayers: () => void;
  getSelectedPlayers: () => Player[];
}

const PlayerContext = createContext<PlayerContextType | undefined>(undefined);

export function PlayerProvider({ children }: { children: ReactNode }) {
  const [players, setPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPlayerIds, setSelectedPlayerIds] = useState<Set<string>>(new Set());
  const { isAuthenticated, loading: authLoading } = useAuth();

  useEffect(() => {
    // Only fetch players once auth is loaded and user is authenticated
    if (!authLoading && isAuthenticated) {
      fetchPlayers();
    } else if (!authLoading && !isAuthenticated) {
      // User is not authenticated, stop loading
      setLoading(false);
    }
  }, [authLoading, isAuthenticated]);

  async function fetchPlayers() {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.getPlayers();
      setPlayers(response.players);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch players');
    } finally {
      setLoading(false);
    }
  }

  async function addPlayer(name: string, favoritePosition?: Position): Promise<Player> {
    const response = await apiClient.createPlayer({
      name,
      favorite_position: favoritePosition || null,
    });

    setPlayers((prev) => [...prev, response.player]);
    return response.player;
  }

  async function updatePlayer(
    id: string,
    updates: {
      name?: string;
      favorite_position?: Position;
      attributes?: Partial<PlayerAttributes>;
    }
  ): Promise<Player> {
    // Find current player
    const currentPlayer = players.find((p) => p.id === id);
    if (!currentPlayer) {
      throw new Error('Player not found');
    }

    // Merge attributes
    const mergedAttributes = updates.attributes
      ? { ...currentPlayer.attributes, ...updates.attributes }
      : currentPlayer.attributes;

    console.log('[PlayerContext] Updating player:', id);
    console.log('[PlayerContext] Current attributes:', currentPlayer.attributes);
    console.log('[PlayerContext] Update attributes:', updates.attributes);
    console.log('[PlayerContext] Merged attributes:', mergedAttributes);

    // Update via API - backend will calculate v2.0 ratings
    console.log('[PlayerContext] Calling API with:', {
      name: updates.name,
      favorite_position: updates.favorite_position,
      attributes: mergedAttributes,
    });
    const response = await apiClient.updatePlayer(id, {
      name: updates.name,
      favorite_position: updates.favorite_position,
      attributes: mergedAttributes,
    });
    console.log('[PlayerContext] API response:', response);

    // Update local state with response from backend (which has correct v2.0 ratings)
    setPlayers((prev) =>
      prev.map((p) =>
        p.id === id
          ? {
              ...p,
              ...response.player,
            }
          : p
      )
    );

    return response.player;
  }

  async function deletePlayer(id: string) {
    await apiClient.deletePlayer(id);
    setPlayers((prev) => prev.filter((p) => p.id !== id));
    setSelectedPlayerIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
  }

  async function bulkDeletePlayers(ids: string[]) {
    await apiClient.bulkDeletePlayers(ids);
    setPlayers((prev) => prev.filter((p) => !ids.includes(p.id)));
    setSelectedPlayerIds((prev) => {
      const newSet = new Set(prev);
      ids.forEach((id) => newSet.delete(id));
      return newSet;
    });
  }

  function togglePlayerSelection(id: string) {
    setSelectedPlayerIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }

  function selectAllPlayers() {
    setSelectedPlayerIds(new Set(players.map((p) => p.id)));
  }

  function deselectAllPlayers() {
    setSelectedPlayerIds(new Set());
  }

  function getSelectedPlayers(): Player[] {
    return players.filter((p) => selectedPlayerIds.has(p.id));
  }

  const value: PlayerContextType = {
    players,
    loading,
    error,
    selectedPlayerIds,
    fetchPlayers,
    addPlayer,
    updatePlayer,
    deletePlayer,
    bulkDeletePlayers,
    togglePlayerSelection,
    selectAllPlayers,
    deselectAllPlayers,
    getSelectedPlayers,
  };

  return <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>;
}

export function usePlayers() {
  const context = useContext(PlayerContext);
  if (context === undefined) {
    throw new Error('usePlayers must be used within a PlayerProvider');
  }
  return context;
}
